import { useEffect, useState, useCallback } from "react";
import Layout from "@/components/Layout";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Coins, Wallet, Download, Filter, RefreshCw } from "lucide-react";

const gr = (n) => (n || 0).toLocaleString("tr-TR", { minimumFractionDigits: 4, maximumFractionDigits: 4 });
const usd = (n) => (n || 0).toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const money = (n) => (n || 0).toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const dmy = (s) => { if (!s) return "—"; const [y, m, d] = s.split("-"); return d ? `${d}.${m}.${y}` : s; };

export default function AltinMuhasebe() {
  const [data, setData] = useState(null);
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [musteri, setMusteri] = useState("");
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (start) params.start = start;
      if (end) params.end = end;
      if (musteri) params.musteri = musteri;
      const { data } = await api.get("/altin/muhasebe", { params });
      setData(data);
    } catch (e) { toast.error("Muhasebe verisi yüklenemedi"); }
    finally { setLoading(false); }
  }, [start, end, musteri]);

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const exportExcel = async () => {
    try {
      const params = {};
      if (start) params.start = start;
      if (end) params.end = end;
      if (musteri) params.musteri = musteri;
      const res = await api.get("/altin/muhasebe/export/excel", { params, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = "altin_muhasebe.xlsx";
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error("Excel indirilemedi"); }
  };

  const clearFilters = () => { setStart(""); setEnd(""); setMusteri(""); setTimeout(load, 0); };

  const hareketler = data?.hareketler || [];

  return (
    <Layout>
      <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
        <div>
          <div className="kicker text-[#B8933F] flex items-center gap-2"><Coins className="w-4 h-4" /> ALTIN MUHASEBE</div>
          <h1 className="font-heading text-3xl font-bold tracking-tight text-[#26231E]">Kasa Hareketleri</h1>
          <p className="text-sm text-[#8C8377] mt-1">Tüm gram altın tahsilatları (peşinat + taksit) tarih ve müşteri bazlı. Son güncelleme: <b>{data?.guncelleme || "—"}</b></p>
        </div>
        <button onClick={exportExcel} data-testid="altin-muhasebe-excel" className="inline-flex items-center gap-2 px-4 py-2 border border-[#E7DECC] bg-white rounded-sm text-sm font-medium hover:bg-[#EFE7D6] transition-colors duration-150">
          <Download className="w-4 h-4 text-[#2F6E4E]" /> Excel
        </button>
      </div>

      {/* Özet kartları */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-[#26231E] text-white rounded-sm p-4" data-testid="altin-muhasebe-toplam-gram">
          <div className="kicker text-[#B8933F] flex items-center gap-1"><Wallet className="w-3.5 h-3.5" /> TOPLAM KASA (GRAM)</div>
          <div className="font-heading font-bold text-2xl mt-1 tabular-nums">{gr(data?.toplam_gram)} <span className="text-sm font-normal">gr</span></div>
        </div>
        <Card label="PEŞİNAT (GRAM)" value={`${gr(data?.pesinat_gram)} gr`} accent="#7C5E9B" />
        <Card label="TAKSİT (GRAM)" value={`${gr(data?.odeme_gram)} gr`} accent="#2F6E4E" />
        <Card label="₺ KARŞILIĞI" value={`${money(data?.toplam_try_karsiligi)} ₺`} accent="#B8933F" />
      </div>

      {/* Filtreler */}
      <div className="bg-white border border-[#E7DECC] rounded-sm p-4 mb-4 flex flex-wrap items-end gap-3" data-testid="altin-muhasebe-filters">
        <div>
          <label className="kicker block mb-1">BAŞLANGIÇ</label>
          <input type="date" value={start} onChange={(e) => setStart(e.target.value)} data-testid="altin-muhasebe-start" className="px-2 py-1.5 border border-[#E7DECC] rounded-sm text-sm" />
        </div>
        <div>
          <label className="kicker block mb-1">BİTİŞ</label>
          <input type="date" value={end} onChange={(e) => setEnd(e.target.value)} data-testid="altin-muhasebe-end" className="px-2 py-1.5 border border-[#E7DECC] rounded-sm text-sm" />
        </div>
        <div className="min-w-[180px]">
          <label className="kicker block mb-1">MÜŞTERİ</label>
          <select value={musteri} onChange={(e) => setMusteri(e.target.value)} data-testid="altin-muhasebe-musteri" className="w-full px-2 py-1.5 border border-[#E7DECC] rounded-sm text-sm bg-white">
            <option value="">Tümü</option>
            {(data?.musteriler || []).map((m) => <option key={m} value={m}>{m}</option>)}
          </select>
        </div>
        <button onClick={load} disabled={loading} data-testid="altin-muhasebe-apply" className="inline-flex items-center gap-2 px-4 py-2 bg-[#B8933F] hover:bg-[#9C7A33] text-white rounded-sm text-sm font-medium disabled:opacity-50">
          {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Filter className="w-4 h-4" />} Filtrele
        </button>
        <button onClick={clearFilters} className="px-3 py-2 border border-[#E7DECC] rounded-sm text-sm text-[#8C8377] hover:text-[#26231E]">Temizle</button>
      </div>

      {/* Hareket tablosu */}
      <div className="bg-white border border-[#E7DECC] rounded-sm overflow-auto" data-testid="altin-muhasebe-table">
        <table className="w-full text-sm">
          <thead className="bg-[#F3ECDD]">
            <tr className="border-b border-[#E7DECC]">
              <th className="px-3 py-2.5 kicker text-left">Tarih</th>
              <th className="px-3 py-2.5 kicker text-left">Müşteri</th>
              <th className="px-3 py-2.5 kicker text-left">Sözleşme</th>
              <th className="px-3 py-2.5 kicker text-left">Ürün</th>
              <th className="px-3 py-2.5 kicker text-left">Tür</th>
              <th className="px-3 py-2.5 kicker text-right">USD</th>
              <th className="px-3 py-2.5 kicker text-right">Kur ($/gr)</th>
              <th className="px-3 py-2.5 kicker text-right">Gram Altın</th>
            </tr>
          </thead>
          <tbody>
            {hareketler.length === 0 ? (
              <tr><td colSpan={8} className="px-3 py-10 text-center text-[#8C8377]">Bu kriterlerde hareket yok.</td></tr>
            ) : hareketler.map((h, i) => (
              <tr key={i} className="border-b border-[#E7DECC] hover:bg-[#F3ECDD]" data-testid="altin-muhasebe-row">
                <td className="px-3 py-2 tabular-nums">{dmy(h.tarih)}</td>
                <td className="px-3 py-2 font-medium">{h.musteri}</td>
                <td className="px-3 py-2 text-[#8C8377]">{h.sozlesme_no || "—"}</td>
                <td className="px-3 py-2">{h.urun || "—"}</td>
                <td className="px-3 py-2">
                  <span className="text-xs font-semibold px-2 py-0.5 rounded-sm" style={h.tur === "Peşinat" ? { color: "#7C5E9B", background: "#7C5E9B12" } : { color: "#2F6E4E", background: "#2F6E4E12" }}>{h.tur}</span>
                </td>
                <td className="px-3 py-2 text-right tabular-nums">{usd(h.usd)} $</td>
                <td className="px-3 py-2 text-right tabular-nums text-[#8C8377]">{usd(h.kur)}</td>
                <td className="px-3 py-2 text-right tabular-nums text-[#B8933F] font-medium">{gr(h.gram)} gr</td>
              </tr>
            ))}
          </tbody>
          {hareketler.length > 0 && (
            <tfoot>
              <tr className="bg-[#F3ECDD] border-t-2 border-[#E7DECC] font-semibold">
                <td className="px-3 py-2.5" colSpan={5}>TOPLAM ({hareketler.length} hareket)</td>
                <td className="px-3 py-2.5 text-right tabular-nums">{usd(data?.toplam_usd)} $</td>
                <td className="px-3 py-2.5"></td>
                <td className="px-3 py-2.5 text-right tabular-nums text-[#B8933F]">{gr(data?.toplam_gram)} gr</td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </Layout>
  );
}

function Card({ label, value, accent }) {
  return (
    <div className="bg-white border border-[#E7DECC] rounded-sm p-4">
      <div className="kicker">{label}</div>
      <div className="font-heading font-bold text-lg mt-1 tabular-nums" style={accent ? { color: accent } : undefined}>{value}</div>
    </div>
  );
}
